import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/core/services/api';
import {
  HistoricalObservation,
  SynapseService,
} from 'src/app/core/services/synapse';
import { api } from 'src/app/models/api-url.model';
import { Ticket } from 'src/app/models/ticket/ticket.model';
import { isTicketSupport } from 'src/app/models/ticket/ticket.util';
import { HistoricalObservationsModalComponent } from '../historical-observations-modal/historical-observations-modal.component';

@Component({
  selector: 'app-ticket-iq-observation',
  templateUrl: './ticket-iq-observation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketIqObservationComponent {
  readonly ticket = input.required<Ticket>();

  readonly isLoading = signal(false);

  readonly summary = signal<string | null>(null);

  readonly summaryUpdatedAt = signal<Date | null>(null);

  readonly historicalObservations = signal<HistoricalObservation[]>([]);

  private readonly destroyRef = inject(DestroyRef);

  private readonly iqService = inject(SynapseService);

  private readonly apiService = inject(ApiService);

  private readonly dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      const ticket = this.ticket();
      if (ticket) untracked(() => this.loadData());
    });
  }

  openSummaryModal(serviceName?: string): void {
    this.dialog.open(HistoricalObservationsModalComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {
        observations: this.historicalObservations(),
        serviceName,
      },
    });
  }

  readonly updatedLabel = computed(() =>
    this.summaryUpdatedAt() ? 'Updated just now' : null,
  );

  readonly fallbackSummary = computed(() => {
    const ticket = this.ticket();
    const subject = ticket?.Subject || 'This ticket';
    const service = ticket?.Service__r?.Name;
    const location = ticket?.Service_Location_Address__c;
    const context = [service, location].filter(Boolean).join(' at ');

    return context
      ? `${subject}. Related service context: ${context}.`
      : `${subject}. No AI summary is available yet.`;
  });

  readonly summaryText = computed(
    () => this.summary() || this.fallbackSummary(),
  );

  readonly historicalObservationLabel = computed(() => {
    const count = this.historicalObservations().length;
    return `${count} historical ${count === 1 ? 'update' : 'updates'}`;
  });

  readonly actionItemCount = computed(
    () =>
      new Set(
        this.historicalObservations()
          .map((observation) => observation.actionLabel)
          .filter(Boolean),
      ).size,
  );

  readonly actionItemLabel = computed(() => {
    const count = this.actionItemCount();
    return `${count} ${count === 1 ? 'action item' : 'action items'}`;
  });

  readonly hasHistoricalObservations = computed(
    () => this.historicalObservations().length > 0,
  );

  readonly historicalObservationsButtonLabel = computed(() => {
    const serviceName = this.ticket()?.Service__r?.Name;
    const serviceContext = serviceName ? ` for ${serviceName}` : '';
    return `View historical IQ observations${serviceContext}`;
  });

  loadHistoricalObservations(nit: string): void {
    const url = `${api.getHistoricalObservationsByNIT}/${nit}`;
    this.apiService
      .get(url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          this.historicalObservations.set(
            Array.isArray(data?.observations)
              ? data.observations.map((obs: any) => ({
                  ...obs,
                  date: this.formatDate(obs.date),
                  beginTime: this.formatTime(obs.begin_time ?? obs.beginTime),
                  endTime: this.formatTime(obs.end_time ?? obs.endTime),
                  actionLabel: obs.actionLabel ?? obs.action_label,
                }))
              : [],
          );
        },
        error: () => {
          this.historicalObservations.set([]);
        },
      });
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${month}/${day}/${year}`;
  }

  private formatTime(timeString: string): string {
    return timeString.slice(0, 5);
  }

  loadData(): void {
    const ticket = this.ticket();
    this.isLoading.set(true);

    if (!isTicketSupport(ticket)) {
      this.isLoading.set(false);
      this.summary.set(null);
      this.summaryUpdatedAt.set(null);
      this.historicalObservations.set([]);
      return;
    }

    if (ticket?.Service__r?.Name) {
      this.loadHistoricalObservations(ticket.Service__r.Name);
    }

    this.iqService
      .getIQTicketSummary(ticket.CaseNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const summary = (data?.[0]?.summary as string) || null;
          this.isLoading.set(false);
          this.summary.set(summary);
          this.summaryUpdatedAt.set(summary ? new Date() : null);
        },
        error: () => {
          this.isLoading.set(false);
          this.summary.set(null);
          this.summaryUpdatedAt.set(null);
        },
      });
  }
}
